import React, { useEffect, useMemo, useState } from 'react';
import { videosApi } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Play, Lock, Layers } from 'lucide-react';
import { logger } from '../lib/logger';

// Canonical Apex Six cores in the order Coach Lewis teaches them.
// `key` matches the `apex_core` value stored on each video document.
const APEX_SIX = [
  {
    key: '3_breath_reset',
    framework: 'The 3-Breath Reset',
    section_title: 'Find Your Reset',
    eyebrow: 'Core 1',
    blurb:
      'Three breaths between every play. Long exhale, anchor breath, power inhale + cue word. Watch these to build the off-court habit that wins the in-game moment.',
  },
  {
    key: 'abc_confidence_stack',
    framework: 'The ABC Confidence Stack',
    section_title: 'Build Your Confidence',
    eyebrow: 'Core 2',
    blurb:
      'Anchor, Belief, Cue \u2014 90 seconds to walk into competition feeling like the version of you that already won. These talks sharpen each layer.',
  },
  {
    key: 'green_yellow_red',
    framework: 'Green-Yellow-Red Awareness',
    section_title: 'Read the Moment',
    eyebrow: 'Core 3',
    blurb:
      "You can't manage what you can't name. Train the in-game radar that tells you whether you're in your green, yellow, or red zone \u2014 and what to do about it.",
  },
  {
    key: 'camera_drill',
    framework: 'The Camera Drill',
    section_title: 'See It to Believe It',
    eyebrow: 'Core 4',
    blurb:
      'Record the rep in your head before you ever run it on the field. Mental imagery is a real workout \u2014 these videos are the playbook for running it well.',
  },
  {
    key: 'bounce_back_3r',
    framework: 'Bounce-Back 3R',
    section_title: 'Bounce Back Stronger',
    eyebrow: 'Core 5',
    blurb:
      "Recognize the lesson. Reframe the story. Re-engage on the next rep. Mistakes are inevitable \u2014 staying stuck isn't.",
  },
  {
    key: 'performance_pyramid',
    framework: 'The Performance Pyramid',
    section_title: 'Climb the Pyramid',
    eyebrow: 'Core 6',
    blurb:
      'Habits, mindset, purpose \u2014 the long-game architecture that compounds across a season. Build the base layer first, the trophies follow.',
  },
];

function ApexVideoCard({ video, onOpen }) {
  const locked = !!video.locked;
  return (
    <button
      type="button"
      onClick={() => onOpen(video)}
      className="group relative aspect-video w-full overflow-hidden rounded-xl border border-border bg-[#0a0d0e] text-left transition-transform hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004C54]"
      aria-label={`Play ${video.title}${locked ? ' (30-second preview)' : ''}`}
      data-testid={`apex-six-card-${video.video_id}`}
    >
      <img
        src={`https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`}
        alt=""
        className="absolute inset-0 h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0d0e] via-[#0a0d0e]/60 to-transparent" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className={`flex h-14 w-14 items-center justify-center rounded-full border border-white/30 backdrop-blur-sm transition-transform group-hover:scale-110 ${
            locked ? 'bg-white/10' : 'bg-[#004C54]/90'
          }`}
        >
          {locked ? <Lock className="h-5 w-5 text-white" /> : <Play className="ml-0.5 h-5 w-5 text-white" />}
        </div>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-3.5 text-white">
        <div className="line-clamp-2 text-[13.5px] font-semibold leading-tight">{video.title}</div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <Badge
            variant="outline"
            className="border-white/25 bg-white/10 text-[10.5px] font-medium text-white"
          >
            {video.category}
          </Badge>
          {locked && (
            <Badge
              variant="outline"
              className="border-amber-200/60 bg-amber-50/10 text-[10.5px] font-medium text-amber-100"
            >
              30s preview
            </Badge>
          )}
        </div>
      </div>
    </button>
  );
}

function ApexPlayer({ video, onClose }) {
  if (!video) return null;
  const isPreview = !!video.locked;
  const src = `https://www.youtube.com/embed/${video.youtube_id}?autoplay=1&rel=0${
    isPreview ? `&start=0&end=${video.preview_seconds || 30}` : ''
  }`;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="apex-six-player"
    >
      <div
        className="relative w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-10 right-0 text-sm text-white/80 hover:text-white"
          data-testid="apex-six-player-close"
        >
          Close \u2715
        </button>
        <div className="aspect-video w-full overflow-hidden rounded-xl border border-white/15 bg-black">
          <iframe
            title={video.title}
            src={src}
            className="h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="mt-3 text-white">
          <div className="font-semibold leading-tight">{video.title}</div>
          {video.description && (
            <p className="mt-1 text-[13px] leading-relaxed text-white/75">{video.description}</p>
          )}
          {isPreview && (
            <p className="mt-2 text-[12px] text-amber-200">
              You\u2019re watching a 30-second preview. Upgrade to unlock the full talk.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ApexSix() {
  const [data, setData] = useState(null);
  const [openVideo, setOpenVideo] = useState(null);

  useEffect(() => {
    videosApi
      .list()
      .then(setData)
      .catch((e) => {
        logger.error('apex six videos load failed', e);
        setData({ videos: [] });
      });
  }, []);

  // Group videos by apex_core. Videos without an apex_core tag are ignored
  // here (they still appear on the existing Videos page, untouched).
  const grouped = useMemo(() => {
    const byCore = {};
    APEX_SIX.forEach((c) => {
      byCore[c.key] = [];
    });
    (data?.videos || []).forEach((v) => {
      const k = v.apex_core;
      if (k && byCore[k]) {
        byCore[k].push(v);
      }
    });
    return byCore;
  }, [data]);

  return (
    <div className="space-y-8" data-testid="apex-six-page">
      {/* Page header */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#004C54] text-white">
          <Layers className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold leading-tight">Apex Six</h1>
          <p className="text-[13.5px] text-foreground/65">
            Six cores. Six videos each. The full mental performance toolkit, organized the way Coach Lewis teaches it.
          </p>
        </div>
      </div>

      {/* Sections */}
      {data === null && (
        <div className="space-y-6" data-testid="apex-six-loading">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-6 w-48" />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((__, j) => (
                  <Skeleton key={j} className="aspect-video w-full" />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {data !== null &&
        APEX_SIX.map((core) => {
          const videos = grouped[core.key] || [];
          return (
            <section key={core.key} data-testid={`apex-six-section-${core.key}`}>
              <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-[#004C54]">
                    {core.eyebrow} \u00b7 {core.framework}
                  </div>
                  <h2 className="font-heading text-[20px] font-bold leading-tight">{core.section_title}</h2>
                </div>
                <Badge variant="outline" className="border-[#004C54]/25 text-[#003a40]">
                  {videos.length} {videos.length === 1 ? 'video' : 'videos'}
                </Badge>
              </div>
              <p className="mb-4 max-w-3xl text-[13.5px] leading-relaxed text-foreground/65">
                {core.blurb}
              </p>
              {videos.length === 0 ? (
                <Card className="border-dashed border-border">
                  <CardContent className="p-6 text-center text-[13.5px] text-foreground/55">
                    No videos in this core yet. Add them under <span className="font-semibold">Admin \u2192 Videos</span> and tag with <span className="font-mono">{core.key}</span>.
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {videos.map((v) => (
                    <ApexVideoCard key={v.video_id} video={v} onOpen={setOpenVideo} />
                  ))}
                </div>
              )}
            </section>
          );
        })}

      <ApexPlayer video={openVideo} onClose={() => setOpenVideo(null)} />
    </div>
  );
}
